import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Users, Briefcase, FileText, ListChecks, UserPlus, X, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import RecipeEditor from "@/components/RecipeEditor";

const TABS = [
  { key: "panoramica", label: "Panoramica" },
  { key: "ricette", label: "Ricette" },
  { key: "utenti", label: "Utenti" },
  { key: "lavori", label: "Lavori" },
  { key: "audit", label: "Audit log" },
];

export default function Admin() {
  const [tab, setTab] = useState("panoramica");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [audit, setAudit] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [showInvite, setShowInvite] = useState(false);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data));
  }, []);
  const loadRecipes = () => api.get("/admin/recipes").then((r) => setRecipes(r.data.items || []));
  useEffect(() => {
    if (tab === "utenti") api.get("/admin/users").then((r) => setUsers(r.data.items || []));
    if (tab === "lavori") api.get("/admin/jobs").then((r) => setJobs(r.data.items || []));
    if (tab === "audit") api.get("/admin/audit").then((r) => setAudit(r.data.items || []));
    if (tab === "ricette") loadRecipes();
  }, [tab]);

  const openNewRecipe = () => { setEditingId(null); setEditorOpen(true); };
  const openEditRecipe = (id) => { setEditingId(id); setEditorOpen(true); };
  const deleteRecipe = async (id, name) => {
    if (!window.confirm(`Eliminare la ricetta "${name}"? L'operazione è irreversibile.`)) return;
    try {
      await api.delete(`/admin/recipes/${id}`);
      toast.success("Ricetta eliminata");
      loadRecipes();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    }
  };

  return (
    <div>
      <div className="adr-overline mb-3">Pannello di amministrazione</div>
      <h1 className="serif text-3xl sm:text-4xl font-semibold tracking-tight text-[#1A1A1A] mb-8">
        Amministrazione
      </h1>

      <div className="flex gap-1 border-b border-[#E2E8F0] mb-8 overflow-x-auto">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`admin-tab-${t.key}`}
            className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors border-b-2 ${
              tab === t.key
                ? "border-[#0F3D6B] text-[#0F3D6B]"
                : "border-transparent text-[#555555] hover:text-[#1A1A1A]"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "panoramica" && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat icon={Users} label="Utenti" value={stats?.users} />
          <Stat icon={Briefcase} label="Lavori totali" value={stats?.jobs_total} />
          <Stat icon={ListChecks} label="Completati" value={stats?.jobs_succeeded} color="#15803d" />
          <Stat icon={FileText} label="Ricette" value={stats?.recipes} />
        </div>
      )}

      {tab === "ricette" && (
        <>
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-[#555555]">
              {recipes.length} ricette in catalogo. Modificale o creane di nuove con il form builder.
            </p>
            <button onClick={openNewRecipe} className="adr-btn-primary text-sm" data-testid="new-recipe-button">
              <Plus className="w-4 h-4 inline mr-1.5" />
              Nuova ricetta
            </button>
          </div>
          <div className="adr-card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#F2F5F8] text-[#555555] text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3">Nome</th>
                  <th className="text-left px-4 py-3">Slug</th>
                  <th className="text-left px-4 py-3">Categoria</th>
                  <th className="text-left px-4 py-3">v</th>
                  <th className="text-left px-4 py-3">Stato</th>
                  <th className="text-right px-4 py-3">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {recipes.map((r) => (
                  <tr key={r.id} className="border-t border-[#E2E8F0]" data-testid={`recipe-row-${r.slug}`}>
                    <td className="px-4 py-3 font-medium text-[#1A1A1A]">{r.name_it}</td>
                    <td className="px-4 py-3 text-[#555555] font-mono text-xs">{r.slug}</td>
                    <td className="px-4 py-3 capitalize">{r.category}</td>
                    <td className="px-4 py-3 text-[#555555]">v{r.version || 1}</td>
                    <td className="px-4 py-3">
                      <span className="adr-badge" style={{ background: r.is_published ? "rgba(34,197,94,0.12)" : "rgba(85,85,85,0.1)", color: r.is_published ? "#15803d" : "#555555" }}>
                        {r.is_published ? "Pubblicata" : "Bozza"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => openEditRecipe(r.id)} className="text-[#0F3D6B] hover:bg-[#0F3D6B]/10 p-1.5 rounded mr-1" data-testid={`edit-recipe-${r.slug}`} aria-label="Modifica">
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button onClick={() => deleteRecipe(r.id, r.name_it)} className="text-[#C8102E] hover:bg-[#C8102E]/10 p-1.5 rounded" data-testid={`delete-recipe-${r.slug}`} aria-label="Elimina">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {editorOpen && (
            <RecipeEditor
              recipeId={editingId}
              onClose={() => setEditorOpen(false)}
              onSaved={() => { setEditorOpen(false); loadRecipes(); }}
            />
          )}
        </>
      )}


      {tab === "utenti" && (
        <>
          <div className="flex justify-end mb-4">
            <button onClick={() => setShowInvite(true)} className="adr-btn-primary text-sm" data-testid="invite-user-button">
              <UserPlus className="w-4 h-4 inline mr-1.5" />
              Invita utente
            </button>
          </div>
          <div className="adr-card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#F2F5F8] text-[#555555] text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3">Nome</th>
                  <th className="text-left px-4 py-3">Email</th>
                  <th className="text-left px-4 py-3">Ruolo</th>
                  <th className="text-left px-4 py-3">Stato</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-t border-[#E2E8F0]" data-testid={`user-row-${u.email}`}>
                    <td className="px-4 py-3 font-medium text-[#1A1A1A]">{u.full_name}</td>
                    <td className="px-4 py-3 text-[#555555]">{u.email}</td>
                    <td className="px-4 py-3 capitalize">{u.role}</td>
                    <td className="px-4 py-3">
                      <span className="adr-badge" style={{ background: u.is_active ? "rgba(34,197,94,0.12)" : "rgba(85,85,85,0.1)", color: u.is_active ? "#15803d" : "#555555" }}>
                        {u.is_active ? "Attivo" : "Disattivato"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {showInvite && (
            <InviteDialog
              onClose={() => setShowInvite(false)}
              onCreated={(u) => {
                setUsers((prev) => [...prev, u]);
                setShowInvite(false);
              }}
            />
          )}
        </>
      )}

      {tab === "lavori" && (
        <div className="adr-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#F2F5F8] text-[#555555] text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Ricetta</th>
                <th className="text-left px-4 py-3">Utente</th>
                <th className="text-left px-4 py-3">Stato</th>
                <th className="text-left px-4 py-3">Data</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((j) => (
                <tr key={j.id} className="border-t border-[#E2E8F0]">
                  <td className="px-4 py-3 font-medium text-[#1A1A1A]">{j.recipe_name}</td>
                  <td className="px-4 py-3 text-[#555555]">{j.user_email}</td>
                  <td className="px-4 py-3 capitalize">{j.status}</td>
                  <td className="px-4 py-3 text-[#555555]">{new Date(j.created_at).toLocaleString("it-IT")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "audit" && (
        <div className="adr-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#F2F5F8] text-[#555555] text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Quando</th>
                <th className="text-left px-4 py-3">Azione</th>
                <th className="text-left px-4 py-3">Risorsa</th>
                <th className="text-left px-4 py-3">IP</th>
              </tr>
            </thead>
            <tbody>
              {audit.map((a) => (
                <tr key={a.id} className="border-t border-[#E2E8F0]">
                  <td className="px-4 py-3 text-[#555555]">{new Date(a.created_at).toLocaleString("it-IT")}</td>
                  <td className="px-4 py-3 font-medium">{a.action}</td>
                  <td className="px-4 py-3 text-[#555555]">{a.resource_type} {a.resource_id?.slice(0, 8)}</td>
                  <td className="px-4 py-3 text-[#555555]">{a.ip_address || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const Stat = ({ icon: Icon, label, value, color = "#0F3D6B" }) => (
  <div className="adr-card p-5" data-testid={`stat-${label}`}>
    <div className="flex items-center gap-3 mb-3">
      <div className="w-9 h-9 rounded-md flex items-center justify-center" style={{ background: `${color}15` }}>
        <Icon className="w-4 h-4" style={{ color }} />
      </div>
      <span className="text-xs text-[#555555] uppercase tracking-wider">{label}</span>
    </div>
    <div className="serif text-3xl font-semibold text-[#1A1A1A]">{value ?? "—"}</div>
  </div>
);

const InviteDialog = ({ onClose, onCreated }) => {
  const [form, setForm] = useState({ email: "", full_name: "", role: "editor", password: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/invite", form);
      toast.success("Utente invitato");
      onCreated(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="serif text-xl font-semibold">Invita utente</h3>
          <button onClick={onClose} aria-label="Chiudi"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="adr-label">Nome completo</label>
            <input className="adr-input" required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="invite-name" />
          </div>
          <div>
            <label className="adr-label">Email</label>
            <input type="email" className="adr-input" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="invite-email" />
          </div>
          <div>
            <label className="adr-label">Ruolo</label>
            <select className="adr-input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="invite-role">
              <option value="viewer">Viewer</option>
              <option value="editor">Editor</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <div>
            <label className="adr-label">Password iniziale</label>
            <input type="text" className="adr-input" minLength={8} required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="invite-password" />
            <div className="text-xs text-[#555555] mt-1">Min. 8 caratteri. L'utente potrà cambiarla in seguito.</div>
          </div>
          <button type="submit" disabled={loading} className="adr-btn-primary w-full mt-2" data-testid="invite-submit">
            {loading ? "Creazione..." : "Crea utente"}
          </button>
        </form>
      </div>
    </div>
  );
};
